// Admin modal: reassign a stuck order to a different driver, or unassign it.
import { useEffect, useState } from "react";
import api, { extractError } from "@/api/client";
import { X, ArrowRightLeft, Bike, UserMinus, CheckCircle2 } from "lucide-react";

export default function OrderReassignModal({ order, onClose, onDone }) {
  const [drivers, setDrivers] = useState([]);
  const [pick, setPick] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [ok, setOk] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/admin/drivers");
        // Exclude the current driver of the order
        setDrivers((data || []).filter((d) => d.user_id !== order?.driver_id));
      } catch (e) { setErr(extractError(e)); }
    })();
  }, [order?.driver_id]);

  const reassign = async (driverId) => {
    setErr(""); setOk(""); setBusy(true);
    try {
      const { data } = await api.patch(`/admin/orders/${order.id}/reassign`, { driver_id: driverId });
      setOk(driverId ? "تم تحويل الطلب للسائق الجديد" : "تم إلغاء إسناد السائق — الطلب متاح لأي سائق الآن");
      onDone?.(data);
      setTimeout(() => onClose?.(), 1200);
    } catch (e) { setErr(extractError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="reassign-modal">
      <div className="bg-white w-full max-w-lg rounded-3xl talabya-shadow-soft overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="bg-gradient-to-l from-primary-brand to-blue-500 text-white px-5 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <ArrowRightLeft size={18}/>
            <div>
              <div className="font-black text-lg" style={{fontFamily:"Cairo"}}>إدارة سائق الطلب</div>
              <div className="text-[11px] opacity-90">طلب #{order?.code} · {order?.status}</div>
            </div>
          </div>
          <button onClick={onClose} className="w-9 h-9 rounded-lg bg-white/15 hover:bg-white/25 flex items-center justify-center" data-testid="reassign-close">
            <X size={16}/>
          </button>
        </div>

        <div className="p-5">
          {err && <div className="mb-3 text-sm bg-red-50 text-red-600 px-3 py-2 rounded-lg" data-testid="reassign-error">{err}</div>}
          {ok && <div className="mb-3 text-sm bg-green-50 text-green-700 px-3 py-2 rounded-lg flex items-center gap-2" data-testid="reassign-ok"><CheckCircle2 size={14}/>{ok}</div>}

          <div className="mb-4 p-3 rounded-xl bg-[var(--surface-alt)] text-sm">
            <div>الزبون: <b>{order?.customer_name}</b></div>
            <div>الإجمالي: <b className="text-primary-brand">{order?.total} د.ل</b></div>
            <div>السائق الحالي: <b>{order?.driver_id ? "مسند" : "غير مسند"}</b></div>
          </div>

          <button
            onClick={() => reassign(null)}
            disabled={busy || !order?.driver_id}
            className="w-full mb-3 py-3 rounded-xl bg-amber-500 hover:bg-amber-600 text-white font-black disabled:opacity-50 flex items-center justify-center gap-2"
            data-testid="reassign-unassign">
            <UserMinus size={16}/> إلغاء الإسناد وإعادته للسائقين المتاحين
          </button>

          <div className="text-xs font-bold text-[var(--text-secondary)] mb-2">أو أسند إلى سائق آخر:</div>
          <div className="max-h-64 overflow-auto space-y-2">
            <div className="mb-2">
              <input value={pick} onChange={(e) => setPick(e.target.value)}
                placeholder="بحث بالاسم أو الهاتف…"
                className="w-full px-3 py-2 rounded-lg border border-[var(--border)] text-sm"
                data-testid="reassign-search"/>
            </div>
            {drivers
              .filter((d) => !pick || (d.name || "").includes(pick) || (d.phone || "").includes(pick))
              .map((d) => (
              <button key={d.user_id || d.id}
                onClick={() => reassign(d.user_id)}
                disabled={busy}
                className="w-full px-3 py-2.5 rounded-xl border border-[var(--border)] hover:border-primary-brand hover:bg-primary-light flex items-center justify-between text-sm disabled:opacity-50"
                data-testid={`reassign-driver-${d.user_id}`}>
                <div className="flex items-center gap-2">
                  <Bike size={14} className="text-primary-brand"/>
                  <div className="text-right">
                    <div className="font-bold">{d.name}</div>
                    <div className="text-[11px] text-[var(--text-secondary)]">{d.phone || "—"}</div>
                  </div>
                </div>
                <div className="text-[11px]">
                  {d.status === "available" ? <span className="text-green-600 font-black">متاح</span> :
                   d.status === "busy" ? <span className="text-amber-600 font-black">مشغول</span> :
                   <span className="text-gray-500">غير متصل</span>}
                </div>
              </button>
            ))}
            {drivers.length === 0 && <div className="text-center text-xs text-[var(--text-secondary)] py-4">لا يوجد سائقون آخرون</div>}
          </div>
        </div>
      </div>
    </div>
  );
}
